import VirtualGallery from '../VirtualGallery';
import useLatestRead from '../../hooks/useLatestRead';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { buildAssetUrl, generationsAPI, projectsAPI, profileAPI } from '../../services/api';
import { getImageModelLabel } from '../../services/imageModels';
import { useAuth } from '../../context/AuthContext';
import OptimizedImage, { getPreviewImagePath } from '../OptimizedImage';
import './Profile.css';

const Profile = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [generations, setGenerations] = useState([]);
  const [projects, setProjects] = useState([]);
  const [costStats, setCostStats] = useState(null);
  const [hasMoreGenerations, setHasMoreGenerations] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const startRead = useLatestRead();
  const startReferenceRead = useLatestRead();
  const [projectReferences, setProjectReferences] = useState({});
  const [showAllProjects, setShowAllProjects] = useState(false);
  const [loading, setLoading] = useState(true);
  const [selectedGeneration, setSelectedGeneration] = useState(null);
  const [isZoomed, setIsZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [showMoreClicked, setShowMoreClicked] = useState(false);
  const loadMoreRef = useRef(null);
  const originalOverflow = useRef(document.body.style.overflow);
  const BATCH_SIZE = 12;
  const ITEMS_PER_ROW = 3;
  const INITIAL_ROWS = 2;

  const loadGenerations = useCallback(async ({ append = false, offset = 0 } = {}) => {
    const read = startRead();
    if (!read.isCurrent()) return;
    if (append) setLoadingMore(true);
    try {
      const limit = append ? BATCH_SIZE : ITEMS_PER_ROW * INITIAL_ROWS;
      const [response, summary] = await Promise.all([
        generationsAPI.getAll({ limit, offset: append ? offset : 0, signal: read.signal }),
        append ? Promise.resolve(null) : profileAPI.getSummary({ signal: read.signal }),
      ]);
      if (!read.isCurrent()) return;
      const rows = response.data || [];
      setGenerations(prev => append ? [...new Map([...prev, ...rows].map(row => [row.id, row])).values()] : rows);
      setHasMoreGenerations(rows.length === limit);
      if (summary) {
        setProjects(summary.data.projects);
        setCostStats(summary.data.stats);
      }
    } catch (error) {
      if (read.isCurrent()) console.error('Failed to load profile:', error);
    } finally {
      if (read.isCurrent()) { setLoading(false); setLoadingMore(false); }
    }
  }, [startRead]);

  useEffect(() => {
    loadGenerations();
  }, [loadGenerations]);

  const visibleProjects = useMemo(
    () => (showAllProjects ? projects : projects.slice(0, 3)),
    [projects, showAllProjects]
  );

  useEffect(() => {
    const read = startReferenceRead();
    const fetchProjectReferences = async () => {
      if (!visibleProjects.length) {
        setProjectReferences({});
        return;
      }

      const referenceEntries = await Promise.all(
        visibleProjects.map(async (project) => {
          try {
            const response = await projectsAPI.getReferences(project.id, { signal: read.signal, params: { previewLimit: 6 } });
            const references = Array.isArray(response.data?.references)
              ? response.data.references
              : Array.isArray(response.data)
                ? response.data
                : [];
            return [project.id, references];
          } catch (err) {
            if (read.isCurrent()) console.error(`Failed to load references for project ${project.id}`, err);
            return [project.id, []];
          }
        })
      );

      if (!read.isCurrent()) return;
      setProjectReferences((prev) => ({
        ...prev,
        ...Object.fromEntries(referenceEntries),
      }));
    };

    fetchProjectReferences();
  }, [visibleProjects, startReferenceRead]);

  useEffect(() => {
    if (projects.length <= 3 && showAllProjects) {
      setShowAllProjects(false);
    }
  }, [projects.length, showAllProjects]);

  useEffect(() => {
    const sentinel = loadMoreRef.current;
    if (!sentinel || !showMoreClicked || loadingMore || !hasMoreGenerations) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            loadGenerations({ append: true, offset: generations.length });
          }
        });
      },
      { rootMargin: '200px 0px', threshold: 0.1 }
    );

    observer.observe(sentinel);

    return () => observer.disconnect();
  }, [generations.length, showMoreClicked, loadingMore, hasMoreGenerations, loadGenerations]);

  const handleShowMore = () => {
    setShowMoreClicked(true);
    if (!loadingMore && hasMoreGenerations) loadGenerations({ append: true, offset: generations.length });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this generation?')) return;

    try {
      await generationsAPI.delete(id);
      loadGenerations();
    } catch (error) {
      console.error('Failed to delete generation:', error);
    }
  };

  const handleDownloadGeneration = (gen) => {
    if (!gen?.image_path) return;
    const link = document.createElement('a');
    link.href = buildAssetUrl(gen.image_path);
    link.download = `generation-${gen.id}.${(gen.output_format || 'png').toLowerCase()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCopyPrompt = async (prompt) => {
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
    } catch (error) {
      console.error('Failed to copy prompt:', error);
    }
  };

  const openModal = (generation) => {
    setSelectedGeneration(generation);
    setIsZoomed(false);
    setZoomLevel(1);
  };

  const closeModal = () => {
    setSelectedGeneration(null);
    setIsZoomed(false);
    setZoomLevel(1);
  };

  const toggleZoom = () => {
    setIsZoomed((prev) => {
      const next = !prev;
      setZoomLevel(next ? 1.5 : 1);
      return next;
    });
  };

  const handleWheelZoom = (event) => {
    if (!isZoomed) return;
    event.preventDefault();
    const direction = event.deltaY > 0 ? -0.15 : 0.15;

    setZoomLevel((prev) => {
      const next = Math.min(4, Math.max(1, prev + direction));

      if (next <= 1.01) {
        setIsZoomed(false);
        return 1;
      }

      return next;
    });
  };

  useEffect(() => {
    if (selectedGeneration || isZoomed) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = originalOverflow.current;
    }

    return () => {
      document.body.style.overflow = originalOverflow.current;
    };
  }, [selectedGeneration, isZoomed]);

  return (
    <div className="profile-container">
      <div className="profile-header">
        <div>
          <h1>Profile</h1>
          <p className="username">@{user?.username}</p>
        </div>
        <button onClick={logout} className="btn-logout">
          Logout
        </button>
      </div>

      {costStats && (
        <div className="cost-breakdown">
          <h3>Cost Breakdown</h3>
          <div className="cost-stats">
            <div className="cost-stat-item">
              <div className="cost-stat-label">Total Images</div>
              <div className="cost-stat-value">{costStats.totalImages}</div>
            </div>
            <div className="cost-stat-item">
              <div className="cost-stat-label">Total Cost</div>
              <div className="cost-stat-value">€{costStats.totalCost}</div>
            </div>
          </div>
          <div className="cost-breakdown-details">
            <div className="cost-breakdown-row">
              <div className="cost-breakdown-label">
                1K & 2K Images ({costStats.images1k2k})
              </div>
              <div className="cost-breakdown-value">€{costStats.cost1k2k}</div>
            </div>
            <div className="cost-breakdown-row">
              <div className="cost-breakdown-label">
                4K Images ({costStats.images4k})
              </div>
              <div className="cost-breakdown-value">€{costStats.cost4k}</div>
            </div>
          </div>
        </div>
      )}

      <div className="projects-section">
        <h2>Projects</h2>
        {loading ? (
          <p>Loading...</p>
        ) : projects.length === 0 ? (
          <p className="empty-message">No projects yet. Start a project to track it here.</p>
        ) : (
          <div className="projects-grid">
            {visibleProjects.map((project) => {
              const latestDate = project.latestGen
                ? new Date(project.latestGen.created_at).toLocaleString()
                : null;
              const references = projectReferences[project.id] || [];

              return (
                <div
                  key={project.id || project.name}
                  className="project-card"
                  role="button"
                  tabIndex={0}
                  onClick={() => project.id && navigate(`/project/${project.id}`)}
                  onKeyDown={(e) => {
                    if ((e.key === 'Enter' || e.key === ' ') && project.id) {
                      e.preventDefault();
                      navigate(`/project/${project.id}`);
                    }
                  }}
                >
                  <div className="project-references-strip">
                    {references.length > 0 ? (
                      references.map((ref, idx) => (
                        <div key={`${project.id}-ref-${idx}`} className="project-ref-thumb">
                          <OptimizedImage
                            src={getPreviewImagePath(ref)}
                            alt={`Reference ${idx + 1}`}
                          />
                        </div>
                      ))
                    ) : (
                      <div className="project-image-empty">No references yet</div>
                    )}
                  </div>
                  <div className="project-info">
                    <div className="project-title-row">
                      <h3 className="project-name">{project.name}</h3>
                      <div className="project-total">€{project.totalCost.toFixed(2)}</div>
                    </div>
                    <div className="project-meta">
                      <span>{project.totalImages} images</span>
                      {project.latestGen?.resolution && <span>{project.latestGen.resolution}</span>}
                      {latestDate && <span>{latestDate}</span>}
                    </div>
                    {project.latestGen && (
                      <p className="project-latest-prompt">{project.latestGen.prompt}</p>
                    )}
                  </div>
                </div>
              );
            })}
            {projects.length > 3 && (
              <button
                type="button"
                className="btn-see-more-projects"
                onClick={() => setShowAllProjects((prev) => !prev)}
              >
                {showAllProjects ? 'See Less' : 'See More'}
              </button>
            )}
          </div>
        )}
      </div>

      <div className="profile-content">
        <h2>Generation History</h2>
        {loading ? (
          <p>Loading...</p>
        ) : generations.length === 0 ? (
          <p className="empty-message">No generations yet. Start creating!</p>
        ) : (
          <>
            <VirtualGallery className="generations-grid">
              {generations.map((gen, index) => (
                <div
                key={gen.id}
                className="generation-card"
                onClick={() => openModal(gen)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    openModal(gen);
                  }
                }}
              >
                <div className="generation-image">
                  <OptimizedImage
                    src={getPreviewImagePath(gen)}
                    alt={gen.prompt}
                    eager={index < ITEMS_PER_ROW * INITIAL_ROWS}
                  />
                </div>
                <div className="generation-info">
                  <p className="generation-prompt">{gen.prompt}</p>
                  <div className="generation-meta">
                    {gen.model && <span>Modèle: {getImageModelLabel(gen.model)}</span>}
                    <span>{gen.aspect_ratio}</span>
                    <span>{gen.resolution}</span>
                    <span>{gen.output_format.toUpperCase()}</span>
                  </div>
                  <p className="generation-cost">Cost: €{parseFloat(gen.cost || 0).toFixed(2)}</p>
                  {gen.project_name && (
                    <p className="generation-project">Project: {gen.project_name}</p>
                  )}
                  <p className="generation-date">
                    {new Date(gen.created_at).toLocaleString()}
                  </p>
                  <button
                    onClick={() => handleDelete(gen.id)}
                    className="btn-delete-gen"
                    onKeyDown={(e) => e.stopPropagation()}
                    onClickCapture={(e) => e.stopPropagation()}
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
            {!loading && showMoreClicked && hasMoreGenerations && (
              <div className="load-more-sentinel" ref={loadMoreRef} aria-hidden="true" />
            )}
          </VirtualGallery>
          {!loading && hasMoreGenerations && (
            <button type="button" className="btn-show-more" onClick={handleShowMore} disabled={loadingMore}>
              Show More
            </button>
          )}
          </>
        )}
      </div>

      {selectedGeneration && (
        <div className="modal-overlay" onClick={closeModal}>
          <button className="modal-close" onClick={closeModal}>
            ×
          </button>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-image-section">
              <OptimizedImage
                src={selectedGeneration.image_path}
                alt={selectedGeneration.prompt}
                className={`modal-image ${isZoomed ? 'zoomed' : ''}`}
                onClick={toggleZoom}
                onWheel={handleWheelZoom}
                style={{ transform: `scale(${zoomLevel})` }}
                eager
              />
            </div>
            <div className="modal-details-section">
              <button
                onClick={() => handleDownloadGeneration(selectedGeneration)}
                className="btn-modal-download"
              >
                Download
                <span className="btn-icon">⬇</span>
              </button>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Prompt</div>
                <div className="modal-detail-value modal-prompt-row">
                  <span>{selectedGeneration.prompt}</span>
                  <button
                    type="button"
                    className="btn-copy-prompt"
                    onClick={() => handleCopyPrompt(selectedGeneration.prompt)}
                    title="Copy prompt"
                    aria-label="Copy prompt"
                  >
                    ⧉
                  </button>
                </div>
              </div>

              <div className="modal-detail-group">
                <div className="modal-detail-label">Settings</div>
                <div className="modal-meta-tags">
                  {selectedGeneration.model && (
                    <span>Modèle: {getImageModelLabel(selectedGeneration.model)}</span>
                  )}
                  <span>{selectedGeneration.aspect_ratio}</span>
                  <span>{selectedGeneration.resolution}</span>
                  <span>{selectedGeneration.output_format.toUpperCase()}</span>
                </div>
              </div>

              {selectedGeneration.reference_images && selectedGeneration.reference_images.length > 0 && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Reference Images</div>
                  <div className="modal-reference-images">
                    {selectedGeneration.reference_images.map((ref, idx) => (
                      <div key={idx} className="modal-reference-thumbnail" title={ref.reference_type === 'project' ? 'Project Reference' : 'Upload Reference'}>
                        <OptimizedImage
                          src={getPreviewImagePath(ref)}
                          alt={`Reference ${idx + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Cost</div>
                <div className="modal-detail-value">€{parseFloat(selectedGeneration.cost || 0).toFixed(2)}</div>
              </div>

              {selectedGeneration.project_name && (
                <div className="modal-detail-group">
                  <div className="modal-detail-label">Project</div>
                  <div className="modal-detail-value">{selectedGeneration.project_name}</div>
                </div>
              )}

              <div className="modal-detail-group">
                <div className="modal-detail-label">Created</div>
                <div className="modal-detail-value">
                  {new Date(selectedGeneration.created_at).toLocaleString()}
                </div>
              </div>

              <button
                onClick={() => handleDelete(selectedGeneration.id)}
                className="btn-modal-delete"
              >
                Delete
                <span className="btn-icon">🗑</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
